import { Controller } from "@hotwired/stimulus"

declare var $: any;

export default class extends Controller {
  static targets = [
    'multiLanguageAdvancedRadio',
    'multiLanguageSelect',
    'multiLanguageOptions',
    'selectedLanguagesText'
  ]

  declare multiLanguageAdvancedRadioTarget: HTMLInputElement;
  declare multiLanguageSelectTarget: HTMLInputElement;
  declare multiLanguageOptionsTarget: HTMLDivElement;
  declare selectedLanguagesTextTarget: HTMLSpanElement;

  private selectionContainer?: HTMLElement;
  private searchInput?: HTMLInputElement;
  private boundHandleKeydown = (e: KeyboardEvent) => this.handleKeydown(e);
  private isRemovingSelection = false;

  initialize(): void {
    const $select = $(this.multiLanguageSelectTarget).select2({
      placeholder: 'Select languages...'
    })
      .on('change', () => this.updateSelectedLanguagesText())
      .on('select2:select', (e: any) => this.handleLanguageSelection(e))
      .on('select2:unselect', (e: any) => this.handleLanguageUnselection(e))
      .on('select2:open', () => this.handleSelect2Open())
      .on('select2:close', () => this.detachSearchInputListener());

    this.attachSelectionContainerListener();
    this.updateSelectedLanguagesText();
  }

  disconnect(): void {
    this.detachSearchInputListener();
    this.detachSelectionContainerListener();
  }

  toggleMultiLanguageOptions() {
    if (this.multiLanguageAdvancedRadioTarget.checked) {
      this.multiLanguageOptionsTarget.classList.remove('hide');
      const $select = $(this.multiLanguageSelectTarget).select2('destroy').select2({
        placeholder: 'Select languages...'
      })
        .on('change', () => this.updateSelectedLanguagesText())
        .on('select2:select', (e: any) => this.handleLanguageSelection(e))
        .on('select2:unselect', (e: any) => this.handleLanguageUnselection(e))
        .on('select2:open', () => this.handleSelect2Open())
        .on('select2:close', () => this.detachSearchInputListener());
      this.attachSelectionContainerListener();
      this.updateSelectedLanguagesText();
    } else {
      this.multiLanguageOptionsTarget.classList.add('hide');
      this.selectedLanguagesTextTarget.classList.add('hide');
    }
  }

  private handleLanguageSelection(event: any): void {
    const selectedValue = event.params.data.id;
    this.addHiddenField(selectedValue);
    this.updateSelectedLanguagesText();
  }

  private handleLanguageUnselection(event: any): void {
    this.isRemovingSelection = true;
    const unselectedValue = event.params.data.id;
    this.removeHiddenField(unselectedValue);
    this.updateSelectedLanguagesText();
    // Close the dropdown after a short delay to prevent it from reopening
    setTimeout(() => {
      $(this.multiLanguageSelectTarget).select2('close');
      this.isRemovingSelection = false;
    }, 10);
  }

  private addHiddenField(value: string): void {
    const existingField = document.querySelector(`input[name="election[vote_settings_attributes][supported_languages][]"][value="${value}"]`);
    if (!existingField) {
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = 'election[vote_settings_attributes][supported_languages][]';
      hiddenField.value = value;
      this.multiLanguageSelectTarget.parentNode?.appendChild(hiddenField);
    }
  }

  private removeHiddenField(value: string): void {
    const field = document.querySelector(`input[name="election[vote_settings_attributes][supported_languages][]"][value="${value}"]`);
    if (field) {
      field.remove();
    }
  }

  private updateSelectedLanguagesText(): void {
    const selectedData: any = $(this.multiLanguageSelectTarget).select2('data');
    let selectedTexts: string[] = [];

    if (Array.isArray(selectedData)) {
      selectedTexts = selectedData.map((option: { text: string }) => option.text);
    }

    if (selectedTexts.length > 0) {
      this.selectedLanguagesTextTarget.innerHTML = `<strong>Selected Languages:</strong> ${selectedTexts.join(', ')}`;
      this.selectedLanguagesTextTarget.classList.remove('hide');
    } else {
      this.selectedLanguagesTextTarget.innerHTML = '';
      this.selectedLanguagesTextTarget.classList.add('hide');
    }
  }

  private attachSelectionContainerListener(): void {
    const container = (this.multiLanguageSelectTarget.nextElementSibling as HTMLElement)
      ?.querySelector('.select2-selection') as HTMLElement | null;

    if (container && container !== this.selectionContainer) {
      this.detachSelectionContainerListener();
      this.selectionContainer = container;
      this.selectionContainer.addEventListener('keydown', this.boundHandleKeydown, true);
    }
  }

  private detachSelectionContainerListener(): void {
    if (this.selectionContainer) {
      this.selectionContainer.removeEventListener('keydown', this.boundHandleKeydown, true);
      this.selectionContainer = undefined;
    }
  }

  private handleSelect2Open(): void {
    if (!this.isRemovingSelection) {
      this.attachSearchInputListener();
    }
  }

  private attachSearchInputListener(): void {
    const input = document.querySelector('.select2-container--default .select2-search__field') as HTMLInputElement | null;
    if (input) {
      this.searchInput = input;
      this.searchInput.addEventListener('keydown', this.boundHandleKeydown, true);
    }
  }

  private detachSearchInputListener(): void {
    if (this.searchInput) {
      this.searchInput.removeEventListener('keydown', this.boundHandleKeydown, true);
      this.searchInput = undefined;
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Backspace') return;

    const target = event.target as HTMLElement;
    const isSearchField = target.classList.contains('select2-search__field');
    const currentSearchVal =
      isSearchField ? (target as HTMLInputElement).value : (this.searchInput?.value ?? '');

    const hasSearchText = !!currentSearchVal && currentSearchVal.length > 0;

    if (hasSearchText) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    const $select2 = $(this.multiLanguageSelectTarget);
    const selectedData: any[] = $select2.select2('data') || [];

    if (selectedData.length > 0) {
      const lastSelected = selectedData[selectedData.length - 1];
      const container = (this.multiLanguageSelectTarget.nextElementSibling as HTMLElement)
        ?.closest('.select2-container') as HTMLElement | null;

      const lastChoiceRemove = container?.querySelector(
        `.select2-selection__choice[title="${CSS.escape(lastSelected.text)}"] .select2-selection__choice__remove`
      ) as HTMLElement | null;

      if (lastChoiceRemove) {
        lastChoiceRemove.click();
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    }
  }
}
